class CustomCollectionCards extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    const section = this;
    const controller = new AbortController();
    this.controller = controller;
    const listen = (target, name, handler) => target.addEventListener(name, handler, { signal: controller.signal });

    const getVariants = (dialog) => {
      if (dialog.customQuickAddVariants) return dialog.customQuickAddVariants;

      const data = dialog.querySelector('[data-custom-quick-add-variants]');
      if (!data) return [];

      try {
        dialog.customQuickAddVariants = JSON.parse(data.textContent);
      } catch (error) {
        console.error('Unable to read quick-add variants.', error);
        dialog.customQuickAddVariants = [];
      }

      return dialog.customQuickAddVariants;
    };

    const getSelections = (dialog) => {
      const groups = [...dialog.querySelectorAll('[data-custom-quick-add-option-group]')];
      const selections = [];

      groups.forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        const selected = group.querySelector('[data-custom-quick-add-option][aria-pressed="true"]');
        selections[position] = selected?.dataset.optionValue || '';
      });

      return selections;
    };

    const variantMatches = (variant, selections) => {
      return selections.every((value, index) => !value || variant.options[index] === value);
    };

    const updateDialog = (dialog) => {
      if (dialog.querySelector('[data-custom-quick-add-submit][aria-busy="true"]')) return;

      const variants = getVariants(dialog);
      const selections = getSelections(dialog);

      dialog.querySelectorAll('[data-custom-quick-add-option]').forEach((optionButton) => {
        const position = Number(optionButton.dataset.optionPosition);
        const candidateSelections = [...selections];
        candidateSelections[position] = optionButton.dataset.optionValue || '';

        const hasAvailableVariant = variants.some(
          (variant) => variant.available && variantMatches(variant, candidateSelections)
        );

        optionButton.disabled = !hasAvailableVariant;
      });

      const selectedVariant = variants.find((variant) => variantMatches(variant, selections));
      const variantInput = dialog.querySelector('[data-custom-quick-add-variant-id]');
      const submitButton = dialog.querySelector('[data-custom-quick-add-submit]');
      const submitText = submitButton?.querySelector('[data-custom-quick-add-button-text]');

      if (!selectedVariant) {
        if (variantInput) variantInput.value = '';
        if (submitButton) submitButton.disabled = true;
        if (submitText) submitText.textContent = 'Unavailable';
        return;
      }

      dialog.querySelectorAll('[data-custom-quick-add-product-link]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', selectedVariant.id);
        link.href = url.href;
      });

      if (variantInput) variantInput.value = selectedVariant.id;
      if (submitButton) submitButton.disabled = !selectedVariant.available;
      if (submitText) submitText.textContent = selectedVariant.available ? 'Add to basket' : 'Sold out';

      const price = dialog.querySelector('[data-custom-quick-add-price]');
      const comparePrice = dialog.querySelector('[data-custom-quick-add-compare-price]');
      const image = dialog.querySelector('[data-custom-quick-add-image]');

      if (price) price.textContent = selectedVariant.price;

      if (comparePrice) {
        comparePrice.textContent = selectedVariant.comparePrice;
        comparePrice.hidden = !selectedVariant.onSale;
      }

      if (image && selectedVariant.image) {
        image.src = selectedVariant.image;
        image.alt = selectedVariant.imageAlt || '';
      }
    };

    listen(section, 'pointerover', (event) => {
      if (event.pointerType === 'touch') return;

      const card = event.target.closest('.custom-card-link');
      if (!card || !section.contains(card) || card.contains(event.relatedTarget)) return;

      const images = card.querySelectorAll('.custom-card__image--desktop');
      if (images.length < 2) return;

      images[0].classList.remove('is-active');
      images[1].classList.add('is-active');
    });

    listen(section, 'pointerout', (event) => {
      const card = event.target.closest('.custom-card-link');
      if (!card || !section.contains(card) || card.contains(event.relatedTarget)) return;

      const images = card.querySelectorAll('.custom-card__image--desktop');
      if (images.length < 2) return;

      images[1].classList.remove('is-active');
      images[0].classList.add('is-active');
    });

    listen(section, 'click', (event) => {
      const openButton = event.target.closest('[data-custom-quick-add-open]');
      if (openButton && section.contains(openButton)) {
        event.preventDefault();
        event.stopPropagation();

        const dialog = document.getElementById(openButton.dataset.customQuickAddOpen);
        if (!(dialog instanceof HTMLDialogElement)) return;

        updateDialog(dialog);
        if (!dialog.open) dialog.showModal();
        return;
      }

      const closeButton = event.target.closest('[data-custom-quick-add-close]');
      if (closeButton && section.contains(closeButton)) {
        event.preventDefault();
        closeButton.closest('dialog')?.close();
        return;
      }

      const optionButton = event.target.closest('[data-custom-quick-add-option]');
      if (optionButton && section.contains(optionButton) && !optionButton.disabled) {
        event.preventDefault();

        const group = optionButton.closest('[data-custom-quick-add-option-group]');
        const dialog = optionButton.closest('dialog');
        if (!group || !dialog) return;
        if (dialog.querySelector('[data-custom-quick-add-submit][aria-busy="true"]')) return;

        const error = dialog.querySelector('[data-custom-quick-add-error]');
        if (error) error.hidden = true;

        group.querySelectorAll('[data-custom-quick-add-option]').forEach((button) => {
          const selected = button === optionButton;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        const selectedValue = group.querySelector('[data-custom-quick-add-selected-value]');
        if (selectedValue) selectedValue.textContent = optionButton.dataset.optionValue || '';

        updateDialog(dialog);
      }
    });

    listen(section, 'click', (event) => {
      if (event.target.matches('[data-custom-quick-add-dialog]')) {
        event.target.close();
      }
    });

    const pending = new Map();
    const timers = new Set();
    const later = (callback, delay) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        if (section.isConnected) callback();
      }, delay);
      timers.add(id);
      return id;
    };
    this.cleanup = () => {
      for (const id of timers) window.clearTimeout(id);
      for (const state of pending.values()) {
        state.button.disabled = false;
        state.button.removeAttribute('aria-busy');
        state.text.textContent = state.defaultText;
      }
      timers.clear();
      pending.clear();
    };

    const finish = (form, successful, message) => {
      const state = pending.get(form);
      if (!state) return;
      pending.delete(form);
      window.clearTimeout(state.timeout);
      timers.delete(state.timeout);
      state.button.removeAttribute('aria-busy');
      state.button.disabled = false;
      state.text.textContent = successful ? 'Added to Basket' : 'Try Again';

      let error = form.querySelector('[data-custom-quick-add-error]');
      if (!error) {
        error = document.createElement('p');
        error.className = 'custom-quick-add__error';
        error.dataset.customQuickAddError = '';
        error.setAttribute('role', 'alert');
        form.append(error);
      }

      error.hidden = successful;
      error.textContent = successful
        ? ''
        : (message || 'We couldn’t confirm this item was added. Check your basket before trying again.');
      if (successful && state.dialog?.open) state.dialog.close();
      if (successful) {
        later(() => {
          if (!pending.has(form)) state.text.textContent = state.defaultText;
        }, 1200);
      }
    };

    const handleCartEvent = (event, successful) => {
      const sourceId = event.detail?.sourceId || event.detail?.data?.sourceId;
      const source = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      for (const [form, state] of pending) {
        if (source !== state.component && sourceId !== state.component?.id) continue;

        if (successful && state.dialog?.open) {
          state.dialog.close();
        }

        if (event.promise && typeof event.promise.then === 'function') {
          event.promise.then(() => finish(form, successful), () => finish(form, false));
        } else {
          finish(form, successful);
        }
      }
    };
    listen(document, 'cart:add', event => handleCartEvent(event, true));
    listen(document, 'cart:update', event => handleCartEvent(event, true));
    listen(document, 'cart:error', event => handleCartEvent(event, false));

    section.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('.custom-quick-add__form')) return;
      if (pending.has(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const button = form.querySelector('[data-custom-quick-add-submit]');
      const text = button?.querySelector('[data-custom-quick-add-button-text]');
      if (!button || !text) return;
      const state = {
        button, text,
        defaultText: button.dataset.defaultText || 'Add to basket',
        component: form.closest('product-form-component'),
        dialog: form.closest('dialog'),
        timeout: null,
      };
      const error = form.querySelector('[data-custom-quick-add-error]');
      if (error) error.hidden = true;

      pending.set(form, state);
      // Leave submission itself to the theme's existing product form component.
      queueMicrotask(() => {
        if (!pending.has(form)) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        text.textContent = 'Adding…';
      });
      state.timeout = later(() => finish(form, false, 'Check basket before retrying'), 20000);
    }, { capture: true, signal: controller.signal });

  }
  disconnectedCallback() {
    this.controller?.abort();
    this.cleanup?.();
    this.controller = null;
    this.cleanup = null;
  }
}
if (!customElements.get('custom-collection-cards')) {
  customElements.define('custom-collection-cards', CustomCollectionCards);
}
